import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from ".";

export type NotificationKind = "info" | "success" | "error" | "warning";

export interface NotificationAction {
  label: string;
  /** Путь внутри приложения, на который ведёт кнопка тоста. */
  to: string;
}

export interface NotificationItem {
  id: string;
  type: NotificationKind;
  title: string;
  message: string;
  createdAt: number;
  /** Время жизни в мс; 0 — тост висит до закрытия. Без значения — по типу. */
  ttl?: number;
  /**
   * Ключ «одного и того же» уведомления (например, задача в конкретном
   * документе): новое уведомление с тем же ключом заменяет старое на месте,
   * а не добавляет ещё один тост.
   */
  key?: string;
  action?: NotificationAction;
  /** Растёт при повторе/замене — тост по нему перезапускает таймер. */
  revision: number;
}

type AddNotificationPayload = Omit<NotificationItem, "id" | "createdAt" | "revision"> & {
  id?: string;
};

interface NotificationState {
  items: NotificationItem[];
}

/** Больше трёх тостов одновременно не читаются — старые уходят. */
const MAX_VISIBLE = 3;

const initialState: NotificationState = {
  items: [],
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<AddNotificationPayload>) => {
      const payload = action.payload;
      const existing = state.items.find((n) =>
        payload.key
          ? n.key === payload.key
          : n.type === payload.type &&
            n.title === payload.title &&
            n.message === payload.message
      );

      if (existing) {
        // Повтор или обновление: тот же тост, свежий текст и таймер, место в
        // стеке — как у нового.
        Object.assign(existing, payload, {
          id: existing.id,
          createdAt: Date.now(),
          revision: existing.revision + 1,
        });
        return;
      }

      const id = payload.id || crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      state.items.push({
        ...payload,
        id,
        createdAt: Date.now(),
        revision: 0,
      });

      while (state.items.length > MAX_VISIBLE) {
        // Сначала вытесняем самое старое не-ошибочное: ошибку пользователь
        // должен успеть прочитать.
        const oldestFirst = [...state.items].sort((a, b) => a.createdAt - b.createdAt);
        const victim = oldestFirst.find((n) => n.type !== "error") ?? oldestFirst[0];
        state.items = state.items.filter((n) => n.id !== victim.id);
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
    },
  },
});

export const { addNotification, removeNotification, clearNotifications } = notificationSlice.actions;

export const selectNotifications = (state: RootState) => state.notifications.items;

export default notificationSlice.reducer;
